/**
 * Phase A — Court Order detail panel.
 *
 * Shows the selected order's full metadata + its sections + a small
 * actions row (Edit · Activate · End). Sections list out as cards
 * with their type, payload summary and inherited effective dates.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import {
  Gavel,
  Building2,
  MapPin,
  CalendarDays,
  Scale,
  FileText,
  Pencil,
  CalendarOff,
  CheckCircle2,
  Plus,
  Trash2,
  GitBranch,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import {
  SECTION_CATEGORIES,
  SECTION_LABELS,
  RICH_PAYLOAD_TYPES,
  canonicalSectionType,
  jurisdictionLabel,
} from "@/components/courtOrders/courtOrderTypes";
import { CourtOrderSectionEditor } from "@/components/courtOrders/CourtOrderSectionEditor";
import {
  SupersedeOrderDialog,
  CourtOrderLineageTimeline,
} from "@/components/courtOrders/CourtOrderVersionControls";

// (legacy label map kept for nostalgia removed — SECTION_LABELS handles it)

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function splitLabel(payload) {
  const t = payload?.split_type;
  if (!t) return "—";
  if (t === "custom") return "Custom";
  return t.replace("_", " / ");
}

function SectionCard({ section, onEdit, onDelete }) {
  const label = SECTION_LABELS[section.type] || section.type;
  const isRich = RICH_PAYLOAD_TYPES.has(canonicalSectionType(section.type));
  return (
    <div
      className="border border-[#D9E1E3] bg-white rounded-md p-3"
      data-testid={`co-detail-section-${section.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#1F9FA4] font-medium">
          {label}
        </div>
        <div className="flex items-center gap-2">
          {section.effective_from ? (
            <div className="text-[10px] text-[#6F828A] font-mono-data">
              {fmtDate(section.effective_from)} → {fmtDate(section.effective_to)}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => onEdit && onEdit(section)}
            className="text-[#6F828A] hover:text-[#2E3A3F] p-0.5"
            aria-label="Edit section"
            data-testid={`co-detail-section-edit-${section.id}`}
          >
            <Pencil className="w-3 h-3" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={() => onDelete && onDelete(section)}
            className="text-[#A48075] hover:text-[#7E3B25] p-0.5"
            aria-label="Delete section"
            data-testid={`co-detail-section-delete-${section.id}`}
          >
            <Trash2 className="w-3 h-3" strokeWidth={1.8} />
          </button>
        </div>
      </div>
      {section.title && section.title !== label ? (
        <div className="text-sm text-[#0B2E32] font-medium mb-1">{section.title}</div>
      ) : null}
      {isRich ? (
        <div className="text-[12px] text-[#3F535A] grid grid-cols-2 gap-2 mt-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
              Split
            </div>
            <div className="font-mono-data">{splitLabel(section.payload)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
              Reimbursement window
            </div>
            <div className="font-mono-data">
              {section.payload?.reimbursement_window_days || 14} days
            </div>
          </div>
        </div>
      ) : (
        <div className="text-[12px] text-[#6F828A] italic">
          The arrangement is captured in the notes below.
        </div>
      )}
      {section.notes ? (
        <div className="text-[12px] text-[#3F535A] mt-2 bg-[#F4F8F9] border border-[#D9E1E3] rounded-sm p-2 whitespace-pre-wrap">
          {section.notes}
        </div>
      ) : null}
    </div>
  );
}

function EditMetadataDialog({ open, onOpenChange, order, onSaved }) {
  const [form, setForm] = useState({
    name: order?.name || "",
    reference: order?.reference || "",
    country_jurisdiction: order?.country_jurisdiction || "",
    issuing_court: order?.issuing_court || "",
    judge: order?.judge || "",
    notes: order?.notes || "",
    // Iter 127.b — Dates are editable directly on the order shell,
    // including clearing effective_to via the "No expiry" checkbox.
    // The backend recomputes status on the next read via
    // `_touch_expiry_status`, so an active order with a future
    // expiry stays active, and one with a past expiry flips to
    // expired the moment the parent lands back on the list.
    effective_from: order?.effective_from || "",
    effective_to: order?.effective_to || "",
    no_expiry: !!order?.no_expiry,
    review_date: order?.review_date || "",
  });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (order) {
      setForm({
        name: order.name || "",
        reference: order.reference || "",
        country_jurisdiction: order.country_jurisdiction || "",
        issuing_court: order.issuing_court || "",
        judge: order.judge || "",
        notes: order.notes || "",
        effective_from: order.effective_from || "",
        effective_to: order.effective_to || "",
        no_expiry: !!order.no_expiry,
        review_date: order.review_date || "",
      });
    }
  }, [order]);

  if (!order) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (form.no_expiry && form.effective_to) {
      toast.error("Uncheck 'No expiry' or clear the expiry date — you can't have both.");
      return;
    }
    if (form.effective_from && form.effective_to && form.effective_to < form.effective_from) {
      toast.error("Effective to cannot be before effective from.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      // Coerce blank date strings to null so the server clears the
      // stored value rather than storing an empty string.
      if (form.no_expiry) payload.effective_to = null;
      else if (!payload.effective_to) payload.effective_to = null;
      if (!payload.review_date) payload.review_date = null;
      if (!payload.effective_from) delete payload.effective_from;
      await api.patch(`/court-orders/${order.id}`, payload);
      toast.success("Order updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto"
        data-testid="co-detail-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Edit order details</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-[12px] text-[#6F828A] -mt-1">
            Updates the order&apos;s metadata. To change the financial split,
            add a new order — that keeps historical compliance records
            reproducible against their original rules.
          </p>
          <div>
            <Label>Order name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="co-detail-edit-name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Court reference</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                data-testid="co-detail-edit-reference"
              />
            </div>
            <div>
              <Label>Country / jurisdiction</Label>
              <select
                className="w-full h-10 border border-[#D9E1E3] rounded-sm px-3 text-sm bg-white"
                value={form.country_jurisdiction}
                onChange={(e) =>
                  setForm({ ...form, country_jurisdiction: e.target.value })
                }
                data-testid="co-detail-edit-country"
              >
                <option value="">—</option>
                <option value="ie">Republic of Ireland</option>
                <option value="ni">Northern Ireland</option>
                <option value="ew">England &amp; Wales</option>
                <option value="sc">Scotland</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Court</Label>
              <Input
                value={form.issuing_court}
                onChange={(e) => setForm({ ...form, issuing_court: e.target.value })}
                placeholder="e.g. Naas District Court"
                data-testid="co-detail-edit-court"
              />
            </div>
            <div>
              <Label>Judge</Label>
              <Input
                value={form.judge}
                onChange={(e) => setForm({ ...form, judge: e.target.value })}
                data-testid="co-detail-edit-judge"
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              data-testid="co-detail-edit-notes"
            />
          </div>
          {/* Iter 127.b — Dates block. Effective from is always
              editable; effective to can be cleared explicitly or via
              the "No expiry" checkbox. Review date is a soft
              reminder (no auto action). Server recomputes status. */}
          <div className="border border-[#D9E1E3] rounded-sm p-3 space-y-3 bg-[#F7FAFA]">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
              Effective dates
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Effective from</Label>
                <Input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  data-testid="co-detail-edit-effective-from"
                />
              </div>
              <div>
                <Label>Effective to (expiry)</Label>
                <Input
                  type="date"
                  value={form.effective_to}
                  disabled={form.no_expiry}
                  onChange={(e) => setForm({ ...form, effective_to: e.target.value, no_expiry: false })}
                  data-testid="co-detail-edit-effective-to"
                />
                <div className="flex items-center gap-3 mt-1">
                  <label className="flex items-center gap-2 text-[11px] text-[#6F828A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.no_expiry}
                      onChange={(e) => setForm({ ...form, no_expiry: e.target.checked, effective_to: e.target.checked ? "" : form.effective_to })}
                      data-testid="co-detail-edit-no-expiry"
                    />
                    No expiry
                  </label>
                  {form.effective_to && !form.no_expiry ? (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, effective_to: "" })}
                      className="text-[11px] text-[#7A2E24] hover:underline"
                      data-testid="co-detail-edit-clear-expiry"
                    >
                      Clear expiry
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            <div>
              <Label>Review date (optional)</Label>
              <Input
                type="date"
                value={form.review_date}
                onChange={(e) => setForm({ ...form, review_date: e.target.value })}
                data-testid="co-detail-edit-review-date"
              />
              <p className="text-[11px] text-[#6F828A] mt-1">
                Reminder date only — nothing auto-happens when it passes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="co-detail-edit-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EndDialog({ open, onOpenChange, order, onEnded }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  if (!order) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/court-orders/${order.id}/end`, { effective_to: date });
      toast.success("Order ended");
      onOpenChange(false);
      onEnded && onEnded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end order.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3]"
        data-testid="co-detail-end-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">End this order</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-[#3F535A]">
            Expenses dated after this date will not be measured against{" "}
            <strong>{order.name}</strong>. Existing snapshots remain unchanged.
          </p>
          <div>
            <Label>Effective to</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              data-testid="co-detail-end-date"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#7E3B25] hover:bg-[#65301F]"
              data-testid="co-detail-end-submit"
            >
              {busy ? "Saving…" : "End order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CourtOrderDetailPanel({ order, relationship, isActive, onChanged, onJumpToOrder }) {
  const [editOpen, setEditOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [sectionEditor, setSectionEditor] = useState({
    open: false,
    mode: "create",
    existing: null,
    type: null,
  });
  const [deleteSection, setDeleteSection] = useState(null);
  // Iter 127.b — visibility state
  const [sharing, setSharing] = useState(false);

  const activate = async () => {
    try {
      await api.post(`/court-orders/${order.id}/activate`, {});
      toast.success("Order activated");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not activate.");
    }
  };

  const shareWithCoParent = async () => {
    // Iter 127.b — confirm because sharing freezes the order.
    if (!window.confirm("Sharing this order with your co-parent freezes it — amendments will need to supersede. Proceed?")) {
      return;
    }
    setSharing(true);
    try {
      await api.post(`/court-orders/${order.id}/share`, {});
      toast.success("Order shared with co-parent. It's now read-only for both of you.");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not share order.");
    } finally {
      setSharing(false);
    }
  };

  const confirmDeleteSection = async () => {
    if (!deleteSection) return;
    try {
      await api.delete(`/court-orders/${order.id}/sections/${deleteSection.id}`);
      toast.success("Section removed");
      setDeleteSection(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove section.");
    }
  };

  return (
    <div
      className="border border-[#D9E1E3] bg-white rounded-md p-4 sm:p-5 space-y-3"
      data-testid="co-detail-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Gavel className="w-4 h-4 text-[#1F9FA4]" strokeWidth={1.7} />
            <h2 className="font-heading text-lg font-medium text-[#0B2E32]">
              {order.name}
            </h2>
            <span
              className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${
                isActive
                  ? "border-[#A7D8DA] bg-[#D6F0EF] text-[#0F5658]"
                  : order.status === "scheduled"
                  ? "border-[#E6CFA2] bg-[#FBF0DC] text-[#6F4E1E]"
                  : order.status === "superseded"
                  ? "border-[#D9CEE6] bg-[#EDE5F4] text-[#4A2F73]"
                  : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"
              }`}
              data-testid="co-detail-status"
            >
              {isActive ? "Active" : order.status}
            </span>
            {order.version_index && order.version_index > 1 ? (
              <span
                className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm border-[#D9E1E3] bg-white text-[#3F535A] font-mono-data"
                data-testid="co-detail-version-pill"
              >
                v{order.version_index}
              </span>
            ) : null}
          </div>
          {order.reference ? (
            <div className="text-[12px] text-[#6F828A] font-mono-data mt-0.5">
              Ref: {order.reference}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Iter 127.b — Once an order is shared with the co-parent
              it becomes immutable. Only Supersede is allowed for
              amendments so both parties always look at the exact
              same version of the shared legal artefact. */}
          {!order.is_shared ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              className="border-[#D9E1E3]"
              data-testid="co-detail-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          ) : (
            <span
              className="text-[11px] px-2 py-1 border border-[#A7D8DA] bg-[#E0F2F2] text-[#0F5658] rounded-sm inline-flex items-center gap-1"
              data-testid="co-detail-shared-lock"
              title="This order has been shared with your co-parent — supersede to amend."
            >
              🔒 Shared · read-only
            </span>
          )}
          {!isActive ? (
            <Button
              variant="outline"
              size="sm"
              onClick={activate}
              className="border-[#A7D8DA] text-[#0F5658] hover:bg-[#E0F2F2]"
              data-testid="co-detail-activate-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Activate
            </Button>
          ) : (
            !order.is_shared && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEndOpen(true)}
                className="border-[#E0B6A8] text-[#7E3B25] hover:bg-[#F8E4DD]"
                data-testid="co-detail-end-btn"
              >
                <CalendarOff className="w-3.5 h-3.5 mr-1.5" /> End
              </Button>
            )
          )}
          {!order.superseded_by_id ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSupersedeOpen(true)}
              className="border-[#1F9FA4] text-[#0F5658] hover:bg-[#E0F2F2]"
              data-testid="co-detail-supersede-btn"
            >
              <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Supersede
            </Button>
          ) : null}
        </div>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] flex items-center gap-1">
            <CalendarDays className="w-3 h-3" /> Effective from
          </div>
          <div className="text-sm font-mono-data text-[#2E3A3F]">
            {fmtDate(order.effective_from)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] flex items-center gap-1">
            <CalendarDays className="w-3 h-3" /> Effective to
          </div>
          <div className="text-sm font-mono-data text-[#2E3A3F]">
            {fmtDate(order.effective_to)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] flex items-center gap-1">
            <Scale className="w-3 h-3" /> Jurisdiction
          </div>
          <div className="text-sm text-[#2E3A3F] truncate" data-testid="co-detail-jurisdiction">
            {jurisdictionLabel(order.country_jurisdiction) ||
              order.jurisdiction ||
              "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] flex items-center gap-1">
            <Building2 className="w-3 h-3" /> Court
          </div>
          <div className="text-sm text-[#2E3A3F] truncate" data-testid="co-detail-court">
            {order.issuing_court || "—"}
            {order.judge ? (
              <span className="text-[#6F828A]"> · {order.judge}</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Version lineage — only renders when there's > 1 version */}
      <CourtOrderLineageTimeline orderId={order.id} onJump={onJumpToOrder} />

      {/* Child scope */}
      <div className="border-t border-[#EEF3F4] pt-3">
        <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">
          Applies to
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {order.all_children ? (
            <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white border-[#D9E1E3] text-[#3F535A]">
              All children in {relationship?.label || "this relationship"}
            </span>
          ) : (order.child_ids || []).length === 0 ? (
            <span className="text-[11px] text-[#6F828A]">No children selected</span>
          ) : (
            (order.child_ids || []).map((cid) => {
              const child = (relationship?.children || []).find((c) => c.id === cid);
              return (
                <span
                  key={cid}
                  className="text-[11px] px-2 py-0.5 rounded-full border bg-white border-[#D9E1E3] text-[#3F535A]"
                >
                  {child?.name || cid}
                </span>
              );
            })
          )}
        </div>
      </div>

      {/* Iter 127.b — Visibility toggle. Orders default to Private
          (owner + household admins only). Sharing with the co-parent
          makes the order visible in their view AND freezes it —
          amendments must supersede so both parties see the exact
          same legal artefact. Un-sharing is intentionally NOT
          exposed here: once shared, only supersede is allowed. */}
      <div
        className="border-t border-[#EEF3F4] pt-3"
        data-testid="co-detail-visibility-wrap"
      >
        <div className="border border-[#D9E1E3] rounded-sm p-3 bg-[#F7FAFA] space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
                Visibility
              </div>
              <div className="text-sm text-[#0B2E32] mt-0.5">
                {order.is_shared
                  ? <>Shared with your co-parent · read-only</>
                  : <>Private · only you and household admins can see this</>}
              </div>
              <div className="text-[11px] text-[#6F828A] mt-1 max-w-prose">
                {order.is_shared
                  ? "The order is now visible to your co-parent. Amendments must supersede so both parties always look at the same version."
                  : "Safety, protection, barring and interim orders often need to stay private. Share only when appropriate."}
              </div>
            </div>
            {!order.is_shared ? (
              <Button
                onClick={shareWithCoParent}
                disabled={sharing}
                className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm h-9"
                data-testid="co-detail-share-btn"
              >
                {sharing ? "Sharing…" : "Share with co-parent"}
              </Button>
            ) : (
              <span
                className="text-[11px] px-2 py-1 border border-[#A7D8DA] bg-[#E0F2F2] text-[#0F5658] rounded-sm"
                data-testid="co-detail-shared-badge"
              >
                Shared
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="border-t border-[#EEF3F4] pt-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] font-medium">
            Sections ({(order.sections || []).length})
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-[#1F9FA4] text-[#0F5658] hover:bg-[#E0F2F2] rounded-sm"
                data-testid="co-detail-add-section"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add section
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-[#D9E1E3] min-w-[230px]"
              data-testid="co-detail-section-menu"
            >
              {SECTION_CATEGORIES.map((cat, ci) => (
                <React.Fragment key={cat.key}>
                  {ci > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A]">
                    {cat.label}
                  </DropdownMenuLabel>
                  {cat.types.map((t) => (
                    <DropdownMenuItem
                      key={t.value}
                      onClick={() => {
                        setSectionEditor({
                          open: true,
                          mode: "create",
                          existing: null,
                          type: t.value,
                        });
                      }}
                      data-testid={`co-detail-section-pick-${t.value}`}
                    >
                      {t.label}
                    </DropdownMenuItem>
                  ))}
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {(order.sections || []).length === 0 ? (
          <div
            className="border border-dashed border-[#D9E1E3] rounded-md p-5 text-[12px] text-[#6F828A] text-center"
            data-testid="co-detail-no-sections"
          >
            <Gavel
              className="w-5 h-5 mx-auto mb-2 text-[#1F9FA4]"
              strokeWidth={1.5}
            />
            <p>
              No sections yet. Use <strong>Add section</strong> above to add
              the specific arrangements this court order makes — parenting,
              contact, financial, welfare, or any other directions.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="co-detail-sections">
            {order.sections.map((s, idx) => (
              <SectionCard
                key={s.id || `sec-${idx}`}
                section={s}
                onEdit={(sec) =>
                  setSectionEditor({
                    open: true,
                    mode: "edit",
                    existing: sec,
                    type: sec.type,
                  })
                }
                onDelete={(sec) => setDeleteSection(sec)}
              />
            ))}
          </div>
        )}
      </div>

      {order.notes ? (
        <div className="border-t border-[#EEF3F4] pt-3">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Notes
          </div>
          <p className="text-[13px] text-[#3F535A] whitespace-pre-wrap leading-relaxed">
            {order.notes}
          </p>
        </div>
      ) : null}

      <EditMetadataDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        order={order}
        onSaved={onChanged}
      />
      <EndDialog
        open={endOpen}
        onOpenChange={setEndOpen}
        order={order}
        onEnded={onChanged}
      />
      <SupersedeOrderDialog
        open={supersedeOpen}
        onOpenChange={setSupersedeOpen}
        order={order}
        onSuperseded={(newOrder) => {
          onChanged && onChanged();
          if (newOrder?.id && onJumpToOrder) onJumpToOrder(newOrder.id);
        }}
      />
      <CourtOrderSectionEditor
        open={sectionEditor.open}
        onOpenChange={(v) => setSectionEditor((s) => ({ ...s, open: v }))}
        orderId={order.id}
        mode={sectionEditor.mode}
        existing={sectionEditor.existing}
        type={sectionEditor.type}
        onSaved={() => {
          setSectionEditor({
            open: false,
            mode: "create",
            existing: null,
            type: null,
          });
          onChanged && onChanged();
        }}
      />
      <AlertDialog
        open={!!deleteSection}
        onOpenChange={(v) => !v && setDeleteSection(null)}
      >
        <AlertDialogContent
          className="bg-white border-[#D9E1E3]"
          data-testid="co-detail-section-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Remove this section?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#3F535A]">
              The section will be removed from this order. Any expenses
              already measured against it keep their stored compliance
              snapshot — historical records remain reproducible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="co-detail-section-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSection}
              className="bg-[#7E3B25] hover:bg-[#65301F]"
              data-testid="co-detail-section-delete-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CourtOrderDetailPanel;
